import { useCallback, useEffect, useMemo } from 'react';

import { useDeviceType } from '@braskai/ui-kit';

import { useBalanceStore } from '@/entities/balance';
import { useClientStorageItem } from '@/entities/clientStorage';

import { queryClient } from '@/shared/config';

import { SCREEN_RECORD_PROMO_STORAGE_KEY, ScreenRecordPromoState } from '../lib/constants';
import { useScreenRecordPromoModalStore } from './useScreenRecordPromoModalStore';

const parseState = (value: string | undefined): ScreenRecordPromoState => {
  if (!value) return {};
  try {
    return JSON.parse(value) as ScreenRecordPromoState;
  } catch {
    return {};
  }
};

export const useScreenRecordPromo = () => {
  const { isMobile } = useDeviceType();
  const { hasActiveSubscription } = useBalanceStore();
  const open = useScreenRecordPromoModalStore((state) => state.open);

  const isEnabled = hasActiveSubscription && !isMobile;

  const { data: storageData, isLoading, setItem } = useClientStorageItem(SCREEN_RECORD_PROMO_STORAGE_KEY, {
    enabled: isEnabled,
  });

  const promoState = useMemo(() => parseState(storageData?.value), [storageData?.value]);
  const hasShown = promoState.hasShownModal === true;

  const markAsShown = useCallback(() => {
    const updatedState: ScreenRecordPromoState = { ...promoState, hasShownModal: true };
    const value = JSON.stringify(updatedState);

    queryClient.setQueryData(['clientStorage', SCREEN_RECORD_PROMO_STORAGE_KEY], {
      key: SCREEN_RECORD_PROMO_STORAGE_KEY,
      value,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    setItem(value);
  }, [promoState, setItem]);

  useEffect(() => {
    if (isMobile || isLoading || hasShown || !hasActiveSubscription) return;

    markAsShown();
    open();
  }, [isMobile, isLoading, hasShown, hasActiveSubscription, markAsShown, open]);
};
